import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'

@customElement('cairn-button')
export class CairnButton extends LitElement {
  static override styles = css`
    :host {
      display: inline-block;
    }

    button {
      display: inline-flex;
      align-items: center;
      gap: var(--cairn-spacing-sm, 8px);
      padding: var(--cairn-spacing-sm, 8px) var(--cairn-spacing-md, 16px);
      border: none;
      border-radius: var(--cairn-border-radius, 8px);
      font-family: inherit;
      font-size: var(--cairn-text-size-sm, 14px);
      font-weight: var(--cairn-font-weight-medium, 500);
      cursor: pointer;
      transition: all var(--cairn-animation-duration, 0.2s)
        var(--cairn-animation-easing, ease-in-out);
      white-space: nowrap;
      text-decoration: none;
      user-select: none;
    }

    button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* Primary variant */
    .primary {
      background: var(--cairn-primary-color, #3b82f6);
      color: white;
    }

    .primary:hover:not(:disabled) {
      background: #2563eb;
    }

    .primary:active:not(:disabled) {
      transform: translateY(1px);
    }

    /* Secondary variant */
    .secondary {
      background: var(--cairn-secondary-color, #64748b);
      color: white;
    }

    .secondary:hover:not(:disabled) {
      background: #475569;
    }

    .secondary:active:not(:disabled) {
      transform: translateY(1px);
    }

    /* Outline variant */
    .outline {
      background: transparent;
      border: 1px solid var(--cairn-primary-color, #3b82f6);
      color: var(--cairn-primary-color, #3b82f6);
    }

    .outline:hover:not(:disabled) {
      background: var(--cairn-primary-color, #3b82f6);
      color: white;
    }

    /* Ghost variant */
    .ghost {
      background: transparent;
      color: var(--cairn-text-color, #1e293b);
    }

    .ghost:hover:not(:disabled) {
      background: var(--cairn-border-color, #e2e8f0);
    }

    /* Danger variant */
    .danger {
      background: var(--cairn-error-color, #dc2626);
      color: white;
    }

    .danger:hover:not(:disabled) {
      background: #b91c1c;
    }

    /* Size variants */
    .small {
      padding: var(--cairn-spacing-xs, 4px) var(--cairn-spacing-sm, 8px);
      font-size: var(--cairn-text-size-xs, 12px);
    }

    .large {
      padding: var(--cairn-spacing-md, 16px) var(--cairn-spacing-lg, 24px);
      font-size: var(--cairn-text-size-md, 16px);
    }

    /* Full width */
    .full-width {
      width: 100%;
      justify-content: center;
    }

    /* Loading state */
    .loading {
      position: relative;
      pointer-events: none;
    }

    .loading::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 16px;
      height: 16px;
      margin: -8px 0 0 -8px;
      border: 2px solid transparent;
      border-top: 2px solid currentColor;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    .loading slot {
      visibility: hidden;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
  `

  @property({ type: String })
  variant: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' = 'primary'

  @property({ type: String })
  size: 'small' | 'medium' | 'large' = 'medium'

  @property({ type: Boolean })
  disabled = false

  @property({ type: Boolean })
  loading = false

  @property({ type: Boolean, attribute: 'full-width' })
  fullWidth = false

  @property({ type: String })
  type: 'button' | 'submit' | 'reset' = 'button'

  private handleClick(e: MouseEvent): void {
    if (this.disabled || this.loading) {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    this.dispatchEvent(
      new CustomEvent('cairn-click', {
        detail: { originalEvent: e },
        bubbles: true,
      })
    )
  }

  override render(): unknown {
    const classes = [
      this.variant,
      this.size,
      this.loading && 'loading',
      this.fullWidth && 'full-width',
    ]
      .filter(Boolean)
      .join(' ')

    return html`
      <button
        class=${classes}
        type=${this.type}
        ?disabled=${this.disabled || this.loading}
        @click=${this.handleClick}
      >
        <slot></slot>
      </button>
    `
  }
}
